import { useState } from 'react';
import axios from 'axios';
import { Download, Upload, Database, AlertTriangle, Check, Mail, Wrench } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Backup = () => {
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [fixingDates, setFixingDates] = useState(false);

  const handleBackup = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/backup/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const date = new Date().toISOString().split('T')[0].replace(/-/g, '');
      link.setAttribute('download', `backup_depuration_${date}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Backup scaricato con successo');
    } catch (error) {
      toast.error('Errore durante il backup');
    } finally {
      setLoading(false);
    }
  };

  const handleBackupEmail = async () => {
    setSendingEmail(true);
    try {
      // Prima scarica il backup
      const response = await axios.get(`${API}/backup/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const date = new Date().toISOString().split('T')[0].replace(/-/g, '');
      const filename = `backup_depuration_${date}.json`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Poi apri il client email
      const today = new Date().toLocaleDateString('it-IT');
      const subject = encodeURIComponent(`Backup Depurazione - ${today}`);
      const body = encodeURIComponent(
        `Backup del sistema depurazione del ${today}.\n\n` +
        `IMPORTANTE: Allega il file "${filename}" appena scaricato a questa email.\n\n` +
        `Il file si trova nella cartella Download del tuo computer.`
      );
      
      // Apri il client email predefinito
      window.location.href = `mailto:blumarlinbivalvi@gmail.com?subject=${subject}&body=${body}`;
      
      toast.success('Backup scaricato! Ora allega il file all\'email che si è aperta.');
    } catch (error) {
      toast.error('Errore durante il backup');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleRestore = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!window.confirm('ATTENZIONE: Il ripristino sovrascrivera TUTTI i dati attuali. Sei sicuro di voler procedere?')) {
      e.target.value = '';
      return;
    }

    setRestoring(true);
    try {
      const fileContent = await file.text();
      const data = JSON.parse(fileContent);
      
      await axios.post(`${API}/backup/restore`, data);
      toast.success('Ripristino completato con successo! Ricarica la pagina.');
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il ripristino');
    } finally {
      setRestoring(false);
      e.target.value = '';
    }
  };

  // Funzione per correggere date e ore
  const handleFixDates = async () => {
    setFixingDates(true);
    try {
      const response = await axios.post(`${API}/bins/fix-movement-dates`);
      const data = response.data;
      if (data.fixed && data.fixed.length > 0) {
        toast.success(`Corretti ${data.fixed.length} movimenti (date e ore)`);
      } else {
        toast.success('Nessuna correzione necessaria - tutti i dati sono OK');
      }
    } catch (error) {
      toast.error('Errore durante la correzione');
    } finally {
      setFixingDates(false);
    }
  };

  // Funzione per riparare database (riattiva bins)
  const handleRepairDatabase = async () => {
    setRepairing(true);
    try {
      const response = await axios.post(`${API}/bins/repair-database`);
      const data = response.data;
      const repairs = data.repairs || {};
      const reactivated = repairs.reactivated_bins?.length || 0;
      const cleaned = repairs.cleaned_empty_bins?.length || 0;
      
      if (reactivated > 0 || cleaned > 0) {
        toast.success(`Riparazione completata: ${reactivated} bins riattivati, ${cleaned} bins puliti`);
      } else {
        toast.success('Database già in ordine - nessuna riparazione necessaria');
      }
    } catch (error) {
      toast.error('Errore durante la riparazione');
    } finally {
      setRepairing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="backup-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="bg-[#FFE500] p-2 rounded-xl">
          <Database className="w-5 h-5 text-[#0A1628]" />
        </div>
        <h2 className="text-2xl font-bold text-white uppercase tracking-tight">Backup e Ripristino</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Backup */}
        <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-6" data-testid="backup-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-blue-500/20 rounded-xl">
              <Download className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Crea Backup</h3>
              <p className="text-sm text-white/50">Scarica una copia di tutti i dati</p>
            </div>
          </div>
          
          <p className="text-sm text-white/60 mb-4">
            Il backup include: bins, fornitori, movimenti e tutte le configurazioni.
            Conserva il file in un luogo sicuro.
          </p>

          <div className="space-y-3">
            <button
              onClick={handleBackup}
              disabled={loading || sendingEmail}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 disabled:opacity-50 font-bold uppercase tracking-wide text-sm transition-colors"
              data-testid="backup-btn"
            >
              <Database className="w-5 h-5" />
              {loading ? 'Creazione backup...' : 'Scarica Backup'}
            </button>

            <button
              onClick={handleBackupEmail}
              disabled={loading || sendingEmail}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 disabled:opacity-50 font-bold uppercase tracking-wide text-sm transition-colors"
              data-testid="backup-email-btn"
            >
              <Mail className="w-5 h-5" />
              {sendingEmail ? 'Preparazione...' : 'Invia Backup via Email'}
            </button>
          </div>
          
          <p className="text-xs text-white/40 mt-3 text-center">
            "Invia via Email" scarica il backup e apre l'email precompilata
          </p>
        </div>

        {/* Ripristino */}
        <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-6" data-testid="restore-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-amber-500/20 rounded-xl">
              <Upload className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Ripristina Backup</h3>
              <p className="text-sm text-white/50">Carica un file di backup</p>
            </div>
          </div>

          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 mb-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-400">
                <strong>Attenzione:</strong> Il ripristino sovrascrivera tutti i dati esistenti. 
                Assicurati di aver fatto un backup prima di procedere.
              </p>
            </div>
          </div>

          <label className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-amber-500 text-white rounded-xl hover:bg-amber-600 cursor-pointer font-bold uppercase tracking-wide text-sm transition-colors">
            <Upload className="w-5 h-5" />
            {restoring ? 'Ripristino in corso...' : 'Seleziona File Backup'}
            <input
              type="file"
              accept=".json"
              onChange={handleRestore}
              disabled={restoring}
              className="hidden"
              data-testid="restore-input"
            />
          </label>
        </div>
      </div>

      {/* Info sincronizzazione */}
      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-5" data-testid="sync-info">
        <div className="flex items-start gap-3">
          <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-bold text-emerald-400">Sincronizzazione Automatica</h4>
            <p className="text-sm text-emerald-400/70 mt-1">
              L'applicazione e sempre online e sincronizzata. Puoi accedere da qualsiasi dispositivo 
              (PC, tablet, telefono) e i dati saranno sempre aggiornati in tempo reale.
            </p>
          </div>
        </div>
      </div>

      {/* Sezione Manutenzione */}
      <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-6" data-testid="maintenance-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-purple-500/20 rounded-xl">
            <Wrench className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Manutenzione Database</h3>
            <p className="text-sm text-white/50">Correggi eventuali problemi nei dati</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Correggi Date e Ore */}
          <div className="bg-white/5 rounded-xl p-4">
            <h4 className="font-bold text-white mb-2">Correggi Date e Ore</h4>
            <p className="text-xs text-white/50 mb-3">
              Corregge le date di ingresso nei movimenti di uscita e ricalcola le ore di depurazione.
            </p>
            <button
              onClick={handleFixDates}
              disabled={fixingDates || repairing}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 font-bold uppercase tracking-wide text-xs transition-colors"
              data-testid="fix-dates-btn"
            >
              {fixingDates ? 'Correzione in corso...' : 'Correggi Date e Ore'}
            </button>
          </div>

          {/* Ripara Database */}
          <div className="bg-white/5 rounded-xl p-4">
            <h4 className="font-bold text-white mb-2">Ripara Bins</h4>
            <p className="text-xs text-white/50 mb-3">
              Riattiva bins con kg disponibili ma status errato. Pulisce bins vuoti e duplicati.
            </p>
            <button
              onClick={handleRepairDatabase}
              disabled={fixingDates || repairing}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 disabled:opacity-50 font-bold uppercase tracking-wide text-xs transition-colors"
              data-testid="repair-db-btn"
            >
              {repairing ? 'Riparazione in corso...' : 'Ripara Bins'}
            </button>
          </div>
        </div>

        <p className="text-xs text-white/40 mt-4 text-center">
          Usa questi strumenti se noti problemi con date, ore o bins che risultano non attivi
        </p>
      </div>
    </div>
  );
};

export default Backup;
